import { useState } from 'react'
import { ToolCallCard, ToolCallHeader } from './tools/_shared/ToolCallHeader'

export interface ToolCallBlockProps {
  toolName: string
  toolCallId: string
  args: Record<string, unknown>
  result?: string
  done: boolean
}

function extractSearchPattern(args: Record<string, unknown>): string | null {
  for (const key of ['query', 'q', 'pattern', 'search', 'name', 'text']) {
    const v = args[key]
    if (typeof v === 'string' && v.trim()) return v
  }
  return null
}

function extractMatchCount(result: string | undefined): number | null {
  if (!result) return null
  try {
    const parsed = JSON.parse(result)
    if (Array.isArray(parsed)) return parsed.length
    if (parsed && typeof parsed === 'object') {
      const r = parsed as Record<string, unknown>
      for (const key of ['matches', 'results', 'hits', 'items', 'entities']) {
        const v = r[key]
        if (Array.isArray(v)) return v.length
      }
      if (typeof r.count === 'number') return r.count
      if (typeof r.total === 'number') return r.total
    }
  } catch {
    // not JSON — ignore
  }
  return null
}

export function ToolCallBlock({ toolName, args, result, done }: ToolCallBlockProps) {
  const [open, setOpen] = useState(false)
  const pattern = extractSearchPattern(args)
  const matchCount = done ? extractMatchCount(result) : null

  const description =
    matchCount === null && !pattern ? null : (
      <>
        {matchCount !== null && <>{matchCount} matches</>}
        {pattern && (
          <>
            {matchCount !== null ? ' for ' : ''}
            <code className="ml-0.5 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
              "{pattern}"
            </code>
          </>
        )}
      </>
    )

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={done ? 'complete' : 'running'}
        toolName={toolName}
        description={description}
        expandable
        open={open}
        onToggle={() => setOpen(o => !o)}
      />
      {open && (
        <div className="flex flex-col gap-1.5 border-t border-border py-2">
          <div className="px-3">
            <pre className="whitespace-pre-wrap font-mono text-[10px] leading-relaxed text-muted-foreground max-md:max-h-40 max-md:overflow-y-auto max-md:text-[11px]">
              {JSON.stringify(args, null, 2)}
            </pre>
          </div>
          {result && (
            <div className="border-t border-dashed border-border px-3 pt-1.5">
              <p className="mb-0.5 text-[10px] font-semibold text-foreground">Result:</p>
              {/* Phone: a raw result wraps into several screens of JSON at
                  343 px, so it scrolls inside a capped box instead. */}
              <pre className="whitespace-pre-wrap font-mono text-[10px] leading-relaxed text-muted-foreground max-md:max-h-72 max-md:overflow-y-auto max-md:text-[11px]">
                {result}
              </pre>
            </div>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}
